import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import type { ApiError } from '@/core/api';
import * as api from './api';
import type { PayrollRunInput, PayslipInput } from './types';

export const payrollKeys = {
  all: ['payroll-runs'] as const,
  lists: () => [...payrollKeys.all, 'list'] as const,
  list: (params: api.RunListParams) => [...payrollKeys.lists(), params] as const,
  detail: (id: string | number) => [...payrollKeys.all, 'detail', id] as const,
  payslips: (runId: number) => [...payrollKeys.all, 'payslips', runId] as const,
};

/** Truthy id that isn't the `-1` "skip" sentinel used by optional lookups. */
function isResolvable(id: string | number): boolean {
  return id !== '' && id !== -1 && id !== '-1' && id != null;
}

export function useRuns(params: api.RunListParams) {
  return useQuery({
    queryKey: payrollKeys.list(params),
    queryFn: () => api.listRuns(params),
    placeholderData: keepPreviousData,
  });
}

export function useRun(id: string | number) {
  return useQuery({
    queryKey: payrollKeys.detail(id),
    queryFn: () => api.getRun(id),
    // Resolves by public_id (string, from the URL) or integer pk; the backend
    // accepts either (ADR-041).
    enabled: isResolvable(id),
  });
}

export function usePayslips(runId: number) {
  return useQuery({
    queryKey: payrollKeys.payslips(runId),
    queryFn: () => api.listPayslips(runId),
    enabled: Number.isInteger(runId) && runId > 0,
  });
}

export function useCreateRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: PayrollRunInput) => api.createRun(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: payrollKeys.lists() });
      toast.success('Payroll run created');
    },
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}

export function useUpdateRun(id: string | number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<PayrollRunInput>) => api.updateRun(id, input),
    onSuccess: (run) => {
      qc.invalidateQueries({ queryKey: payrollKeys.lists() });
      qc.setQueryData(payrollKeys.detail(id), run);
      toast.success('Payroll run updated');
    },
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}

export function useDeleteRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string | number) => api.deleteRun(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: payrollKeys.lists() });
      toast.success('Payroll run deleted');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

export function useFinalizeRun(id: string | number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.finalizeRun(id),
    onSuccess: (run) => {
      qc.setQueryData(payrollKeys.detail(id), run);
      qc.invalidateQueries({ queryKey: payrollKeys.lists() });
      toast.success('Payroll run finalized');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

export function useGenerateFromInvoices(publicId: string | number, runId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.generateFromInvoices(publicId),
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: payrollKeys.payslips(runId) });
      qc.setQueryData(payrollKeys.detail(publicId), result);
      toast.success(
        result.payslips_created > 0
          ? `${result.payslips_created} payslip${result.payslips_created === 1 ? '' : 's'} generated`
          : 'No new signed invoices to pay',
      );
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

export function useCreatePayslip(runId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Omit<PayslipInput, 'payroll_run'>) =>
      api.createPayslip({ ...input, payroll_run: runId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: payrollKeys.payslips(runId) });
      toast.success('Payslip added');
    },
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}
